<template>
  <v-layout row wrap v-bind:class="{ 'pa-0': isConfigMode, 'pa-4': isCaseMode }">
    <v-flex class="mb-4">
      <div class="action-bar">
        <div>
          <v-btn v-if="isConfigMode" @click="newFolderDialog = true" color="secondary"
            class="mt-0 ml-0 mr-3 white--text">
            <v-icon class="mr-2" left dark>add</v-icon>New Folder </v-btn>
          <v-btn v-if="isCaseMode" @click="openBrowser()" color="secondary" class="mt-0 ml-0 mr-2 white--text"> <v-icon
              class="mr-2" left dark>file_upload</v-icon>Upload</v-btn>
        </div>
        <div v-if="hiddenFiles.length > 0" class="hidden-toggle">
          <v-switch id="hideToggle" v-model="showHidden" style="display:inline-block"></v-switch>Show {{
            hiddenFiles.length }} Hidden File{{ hiddenFiles.length > 1 ? "s" : "" }}
        </div>
      </div>
      <p v-if="isConfigMode" class="d-inline-block pt-2 pl-3  pb-0 opacity-54"><strong>Info:</strong> Changes to the
        directory structure in a template do not affect live cases.</p>
      <v-dialog v-model="newFolderDialog" max-width="500">
        <v-card class="pa-4">
          <v-card-title class="pa-0 pb-2">
            <h2>Create a new folder</h2>
          </v-card-title>
          <v-card-text class="px-0 pb-0">
            <v-text-field v-model="newFolderName" box label="Folder name"></v-text-field>
          </v-card-text>
          <v-card-actions class="ma-0 pa-0 pt-1">
            <v-btn @click="createFolder" color="success">Create folder</v-btn>
            <v-btn @click="newFolderDialog = false" color="secondary">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
    <v-flex v-if="getBreadcrumbs.length" xs12 mb-3>
      <v-breadcrumbs class="pa-0" large :items="getBreadcrumbs" divider=">">
        <template v-slot:divider>
          <v-icon>chevron_right</v-icon>
        </template>
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item @click.prevent="navigateToFolder(item)" :disabled="item.disabled">
            {{ item.name }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
    </v-flex>
    <v-flex xs12>
      <v-data-table :loading="isLoading" :headers="documentHeaders" :items="isLoading ? [] : documentTableItems"
        class="doc-table" :custom-sort="defaultListSort" hide-default-footer
        :no-data-text="isConfigMode ? 'No folders available' : 'Drag & drop files here to upload them'"
        :rows-per-page-items="[5, 10, 20, 50, 100]" :pagination.sync="pagination">
        <template v-slot:headers="props">
          <tr>
            <th v-if="isAttachmentMode">
              <v-checkbox v-if="allowSelectMultiple" v-model="allAttachmentsSelected" primary hide-details
                @click.stop="toggleAllAttachments(allAttachmentsSelected)" color="secondary"
                :indeterminate="allAttachmentsSelected"></v-checkbox>
            </th>
            <th class="text-xs-left px-0" v-for="header in props.headers" :key="header.text"
              :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)">
              <v-icon small>arrow_downward</v-icon>
              {{ header.text }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <component :key="props.item.type + '-' + props.item.id" v-bind:is="getListItemType(props.item.type)"
            :listItem="props.item" :documentHeaders="documentHeaders" :isConfigMode="isConfigMode"
            :isAttachmentMode="isAttachmentMode" :isFolderSelectMode="isFolderSelectMode"
            :allowSelectMultiple="allowSelectMultiple" v-on:folder-navigate="navigateToFolder"
            v-on:folder-delete="deleteFolder" v-on:folder-update="updateFolder"></component>
        </template>
      </v-data-table>
    </v-flex>

    <input multiple id="file-input" ref="attachment" type="file" style="display:none" @change="onFileSelected" />
  </v-layout>
</template>

<script>
import DocumentField from "@/components/cases/case-elements/DocumentField.vue"
import FileListItem from "@/components/documents/FileListItem.vue"
import FolderListItem from "@/components/documents/FolderListItem.vue"
import Helpers from "@/helpers.js"
import Loading from "@/components/shared/Loading.vue"
import { mapState } from "vuex"

export default {
  name: "DocumentsTable",
  components: { DocumentField, Loading, FileListItem, FolderListItem },
  props: {
    isAttachmentMode: {
      type: Boolean,
      default: false,
    },
    isCaseMode: {
      type: Boolean,
      default: false,
    },
    isConfigMode: {
      type: Boolean,
      default: false,
    },
    isFolderSelectMode: {
      type: Boolean,
      default: false,
    },
    allowSelectMultiple: {
      type: Boolean,
      default: true,
    },
    caseId: { type: Number },
    documentHeaders: { type: Array },
    files: { type: Array },
  },
  data: () => ({
    newFolderDialog: false,
    newFolderName: "",
    renameDialog: false,
    duplicateDialog: false,
    copyDialog: false,
    deleteDialog: false,
    localpagination: {
      sortBy: "name",
      descending: false,
    },
    showHidden: false,
    selected: [],
    allAttachmentsSelected: false,
    initialLoadActionsComplete: false,
  }),
  computed: {
    ...mapState({
      user: (state) => state.currentUser,
      pathNavigation: (state) => state.cbroute.params.path_navigation,
      caseType: (state) => state.caseTypes.current,
      caseFolders: (state) => state.cases.currentlySelected.caseFolders,
      templatedFolders: (state) => state.caseTypes.current.templatedFolders,
      foldersLoading: (state) => state.folders.documentsTable.loading,
      currentFolderPath: (state) => state.folders.documentsTable.currentFolderPath,
      currentFolder: (state) => state.folders.documentsTable.currentFolder,
      currentCase: (state) => (state && state.cases && state.cases.currentlySelected.case ? state.cases.currentlySelected.case : {}),
      selectedAttachments: (state) => state.folders.selectedAttachments,
      isMoveFileDialogActive: (state) => state.folders.moveFileDialog,
      isCopyFileDialogActive: (state) => state.folders.copyFileDialog,
      navigationFolderHistory: (state) => state.folders.documentsTable.navigationFolderHistory,
      breadcrumbs: (state) => state.folders.documentsTable.breadcrumbs,
      allFileCheckboxSelected: (state) => state.folders.allFileCheckboxSelected,

      // Dialog document folders
      dialogFoldersLoading: (state) => state.folders.dialogDocumentsTable.loading,
      dialogFolders: (state) => state.folders.dialogDocumentsTable.folders,
      dialogCurrentFolderPath: (state) => state.folders.dialogDocumentsTable.currentFolderPath,
      dialogCurrentFolder: (state) => state.folders.dialogDocumentsTable.currentFolder,
      dialogBreadcrumbs: (state) => state.folders.dialogDocumentsTable.breadcrumbs,
    }),
    pagination: {
      get() {
        return {
          ...this.localpagination,
          rowsPerPage: this.user && this.user.me && this.user.me.preferences && this.user.me.preferences.pageSize ? this.user.me.preferences.pageSize : 10,
        }
      },
      set(newPagination) {
        this.localpagination = newPagination
      },
    },
    isLoading() {
      if (this.isFolderSelectMode) {
        return this.dialogFoldersLoading
      } else {
        return this.foldersLoading || !this.initialLoadActionsComplete
      }
    },
    folders() {
      if (this.isConfigMode) return this.templatedFolders
      return this.isFolderSelectMode ? this.dialogFolders : this.caseFolders // document dialog mode reads from different state
    },
    getBreadcrumbs() {
      return this.isFolderSelectMode ? this.dialogBreadcrumbs : this.breadcrumbs
    },
    isDocumentDialogActive() {
      return this.isMoveFileDialogActive || this.isCopyFileDialogActive
    },
    documentTableItems() {
      let folders = []
      if (this.folders) {
        folders = this.folders.map((folder) => Helpers.formatFolder(folder))
      }
      let files = []

      if (Array.isArray(this.files)) {
        files = this.getCurrentFileAttachments(this.files)
      }

      if (this.showHidden !== true) {
        console.log("hidding hidden files", files)
        files = files.filter((f) => { return !f.hidden })
        console.log("without hidden files", files)
      }

      const items = folders.concat(files)
      return this.handleSort(this.pagination, items)
    },
    caseDocumentLocation() {
      return `/documents/${this.user.account.id}/${this.caseType.id}/${this.caseId}`
    },
    hiddenFilesPresent() {
      return this.hiddenFiles.length > 0
    },
    hiddenFiles() {
      if (this.files) {
        return this.files.filter((f) => { return f.hidden })
      } else return []
    }
  },
  methods: {
    async getDocumentTableItems() {
      const parentFolderId = this.currentFolder && this.currentFolder.id ? this.currentFolder.id : null

      await this.$store.dispatch("getFolders", {
        configMode: this.isConfigMode,
        caseTypeId: this.caseType.id,
        parentFolderId,
        caseId: this.currentCase.id,
      })

      this.$store.dispatch("setBreadcrumbs", this.isFolderSelectMode)
    },
    async getDialogDocumentTableItems() {
      const parentFolderId = this.dialogCurrentFolder && this.dialogCurrentFolder.id ? this.dialogCurrentFolder.id : null

      if (this.isFolderSelectMode) {
        await this.$store.dispatch("getFolders", {
          parentFolderId,
          caseId: this.currentCase.id,
          dialogDocumentsTable: this.isFolderSelectMode,
        })
      }
      this.$store.dispatch("setBreadcrumbs", this.isFolderSelectMode)
    },
    getCurrentFileAttachments(files) {
      const currentFolderPath = this.isFolderSelectMode ? this.dialogCurrentFolderPath : this.currentFolderPath
      const currentDirPath = currentFolderPath.endsWith("/") ? currentFolderPath : currentFolderPath + "/"
      const currentFullPath = `${this.caseDocumentLocation}${currentDirPath}`

      if (Array.isArray(files)) {
        return files
          .filter((fa) => {
            const fileDocumentPath = fa.url.replace(fa.filename, "")
            return currentFullPath === fileDocumentPath
          })
          .map((file) => this.formatFileAttachment(file))
      }
      return []
    },
    openBrowser() {
      this.$refs.attachment.click()
    },
    async onFileSelected(event) {
      await this.$store.dispatch("uploadCaseDocument", {
        user: this.user,
        case: this.currentCase,
        files: event.target.files,
        dialogDocumentsTable: this.isAttachmentMode,
      })

      if (this.isAttachmentMode && event.target.files.length && event.target.files[0].name) {
        const currentDirPath = this.currentFolderPath.endsWith("/") ? this.currentFolderPath : this.currentFolderPath + "/"
        const fullAttachmentPath = `${this.caseDocumentLocation}${currentDirPath}${event.target.files[0].name}`

        const newAttachment = this.files.find((a) => a.url === fullAttachmentPath)
        if (newAttachment) {
          const newAttachmentFormatted = this.formatFileAttachment(newAttachment)
          newAttachmentFormatted.selected = true

          if (!this.allowSelectMultiple) this.$store.dispatch("clearFileSelection")
          this.$store.dispatch("toggleFileSelection", { file: newAttachmentFormatted, allowSelectMultiple: this.allowSelectMultiple })
        }
      }
    },
    getListItemType(type) {
      if (type === "folder") return "FolderListItem"
      return "FileListItem"
    },
    defaultListSort(items, index, isDesc) {
      const sortedFiles = this.handleSort(this.pagination, items.filter((i) => i.type === "file"))
      const sortedFolders = this.handleSort(this.pagination, items.filter((i) => i.type === "folder"))
      return sortedFolders.concat(sortedFiles)
    },
    navigateToFolder(folder) {
      if (folder) {
        if (folder.name && folder.path) {
          this.$store.dispatch("setCurrentFolder", { folder, dialogDocumentsTable: this.isFolderSelectMode })
          this.$store.dispatch("setCurrentFolderPath", { folderPath: folder.path, dialogDocumentsTable: this.isFolderSelectMode })
          this.$store.dispatch("setNavigationFolderHistory", { folder, dialogDocumentsTable: this.isFolderSelectMode })
        } else {
          this.$store.dispatch("setCurrentFolderPath", { folderPath: "/", dialogDocumentsTable: this.isFolderSelectMode })
          this.$store.dispatch("setCurrentFolder", { folder: null, dialogDocumentsTable: this.isFolderSelectMode })
        }
      }
    },
    async createFolder() {
      if (this.isFolderValid(this.newFolderName)) {
        await this.$store.dispatch("createTemplatedFolder", {
          folderName: this.newFolderName,
          caseTypeId: this.caseType.id,
          parentFolderId: this.currentFolder && this.currentFolder.id ? this.currentFolder.id : null,
        })
        this.toggleNewFolderDialog(false)
      }
    },
    async deleteFolder(folderId) {
      await this.$store.dispatch("deleteTemplatedFolder", {
        folderId,
        caseTypeId: this.caseType.id,
      })
    },
    async updateFolder({ folderName, folderId, parentFolderId }) {
      if (this.isConfigMode && this.isFolderValid(folderName)) {
        await this.$store.dispatch("updateTemplatedFolder", {
          folderId,
          folderName,
          caseTypeId: this.caseType.id,
        })
      }
    },
    isFolderValid(folderName, folderId = null) {
      if (folderName && folderName.trim()) {
        if (this.isValidFolderName(folderName)) {
          const folderAlreadyExists = this.doesFolderExist(folderName, this.folders, folderId)
          if (!folderAlreadyExists) {
            return true
          } else {
            this.$store.commit("displayFlashMessage", { message: "A folder with this name already exists", severity: "error" })
          }
        } else {
          this.$store.commit("displayFlashMessage", { message: `Folder names should not include '\\', '/' or begin with '.'`, severity: "error" })
        }
      } else {
        this.$store.commit("displayFlashMessage", { message: "Please enter a folder name", severity: "error" })
      }
      return false
    },
    isValidFolderName(folderName) {
      return Helpers.isValidFolderOrFileName(folderName)
    },
    doesFolderExist(folderNameToCheck, folders, folderId = null) {
      return folders.find((f) => f && f.folderName === folderNameToCheck && f.id !== folderId)
    },
    toggleNewFolderDialog(visible) {
      this.newFolderDialog = visible
      if (!visible) this.newFolderName = ""
    },
    toggleAllAttachments(toggle) {
      this.allAttachmentsSelected = !toggle
      const folderFileAttachments = this.getCurrentFileAttachments(this.files)

      if (folderFileAttachments && folderFileAttachments.length) {
        this.$store.dispatch("toggleFilesSelection", { files: folderFileAttachments, selected: this.allAttachmentsSelected })
      }
    },
    changeSort(column) {
      console.log("changeSort", column)
      if (this.localpagination.sortBy === column) {
        this.localpagination.descending = !this.localpagination.descending
      } else {
        this.localpagination.sortBy = column
        this.localpagination.descending = false
      }
    },
    handleSort(pagination, items) {
      return items.sort((a, b) => {
        if (!pagination.descending) {
          return a[pagination.sortBy] > b[pagination.sortBy] ? 1 : -1
        } else {
          return a[pagination.sortBy] < b[pagination.sortBy] ? 1 : -1
        }
      })
    },
    isFileSelected(fileId) {
      return this.selectedAttachments.find((a) => a.id === fileId)
    },
    formatFileAttachment(file) {
      return {
        id: file.id,
        name: file.filename,
        templateDefined: false,
        date: file.updatedAt,
        url: file.url,
        contentType: file.contentType,
        modified: file.userDisplayName,
        size: file.size,
        icon: this.getFileIcon(file.contentType),
        type: "file",
        selected: this.isFileSelected(file.id),
        hidden: file.hidden
      }
    },
    getFileIcon(contentType) {
      let icon = "file"

      if (contentType.includes("csv")) {
        icon = "file-excel"
      } else if (contentType.includes("html")) {
        icon = "file-code"
      } else if (contentType.includes("powerpoint") || contentType.includes("csv")) {
        icon = "file-powerpoint"
      } else if (contentType.includes("rar") || contentType.includes("tar")) {
        icon = "file-archive"
      } else if (contentType.includes("pdf")) {
        icon = "file-pdf"
      } else if (contentType.includes("image")) {
        icon = "file-image"
      } else if (contentType.includes("text")) {
        icon = "file-word"
      }
      return icon
    },
  },
  watch: {
    currentFolderPath: {
      handler: async function (newPath, oldPath) {
        if (!this.isFolderSelectMode) {
          // Handle initial load for non dialog document components
          if (!this.initialLoadActionsComplete) {
            this.$store.dispatch("setFoldersLoading", true)
            this.initialLoadActionsComplete = true // called before action to avoid duplicate call on watch

            // 'view in document tab'
            if (this.pathNavigation) {
              await this.$store.dispatch("navigateFromCase", { caseId: this.caseId, path: this.pathNavigation })
            } else {
              const triggerReload = this.$store.dispatch("resetDocumentState", this.isFolderSelectMode)
              if (triggerReload) {
                // always reload when first enter '/'
                await this.getDocumentTableItems()
              }
            }
          } else {
            // when path changes after initial load
            await this.getDocumentTableItems()
          }
        }
      },
      deep: true,
      immediate: true,
    },
    dialogCurrentFolderPath: {
      handler: async function (newPath, oldPath) {
        if (this.isFolderSelectMode) {
          await this.getDialogDocumentTableItems()
        }
      },
      deep: true,
      immediate: true,
    },
    allFileCheckboxSelected: {
      handler: async function (newVal, oldVal) {
        if (newVal !== this.allAttachmentsSelected) {
          this.allAttachmentsSelected = newVal
        }
      },
    },
  },
}
</script>

<style stylus>
.hidden-toggle {
  display: flex;
  align-items: center;
}

.hidden-toggle .v-input {
  padding: 0;

}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.doc-table {
  table-layout: fixed;
}

.doc-table .doc-check {
  width: 64px;
  padding: 0 0 0 12px !important;
}

.doc-table .doc-primary-td {
  padding: 0 12px !important;
  width: 45%;
  position: relative;
}

.doc-table .td:last-of-type {}

.doc-table .doc-primary-td svg {
  position: absolute;
  left: 12px;
  top: 10px;
}

.doc-table .doc-primary-td .v-menu {
  position: absolute;
  right: 0px;
  top: 14px;
  opacity: 0;
  transition: all 0.3s;
}

.doc-table tr:hover>.doc-primary-td .v-menu {
  opacity: 1;
}

.doc-table .doc-primary-td h3 {
  padding: 0 40px;
}

.doc-table .doc-primary-td h3 a {
  text-decoration: none;
}

.doc-table .doc-primary-td h3 a:hover {
  text-decoration: underline;
}

.doc-table table.v-table thead th:first-child {
  padding: 0 12px !important;
}

.selected-docs svg {
  vertical-align: middle;
}

.selected-docs h3 {
  vertical-align: middle;
  padding-right: 0px;
}
</style>
